import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Input from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import Card from '../../common/Card/Card';
import Notification from '../../common/Notification/Notification';
import { useAuth } from '../../../hooks/useAuth';
import { LoginCredentials } from '../../../types/auth.types';
import GoogleSignInButton from '../GoogleSignInButton/GoogleSignInButton';
import { authErrorToReactNode } from '../authErrorDisplay';

const LoginForm: React.FC = () => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: '',
  });
  
  const navigate = useNavigate();
  const { login, loading, error, clearError } = useAuth();
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev: LoginCredentials) => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await login(credentials);
  };
  
  const handleGoogleLoginSuccess = () => {
    navigate('/dashboard');
  };
  
  const handleGoogleLoginFailure = (error: Error) => {
    console.error('Google login failed:', error);
  };
  
  return (
    <Card className="max-w-md mx-auto">
      <div className="flex flex-col items-center mb-6 mt-4">
        <h2 className="text-2xl font-bold text-gray-800">Login to WebLeads 🚀</h2>
      </div>
      
      {error && (
        <Notification
          type="error"
          message={authErrorToReactNode(error)}
          onClose={clearError}
        />
      )}
      
      <GoogleSignInButton
        onSuccess={handleGoogleLoginSuccess}
        onFailure={handleGoogleLoginFailure}
      />
      
      <p className="text-xs text-gray-500 text-center mt-2">
        By signing in with Google, you agree to our{' '}
        <a 
          href="https://www.webleads.site/terms" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Terms of Service
        </a>
        {' '}and{' '}
        <a 
          href="https://www.webleads.site/privacy" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Privacy Policy
        </a>
      </p>
      
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">or</span>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          id="email"
          name="username"
          type="email"
          label="Email"
          value={credentials.username}
          onChange={handleChange}
          required
          autoComplete="email"
        />
        
        <Input
          id="password"
          name="password"
          type="password"
          label="Password"
          value={credentials.password}
          onChange={handleChange}
          required
          autoComplete="current-password"
        />
        
        <div className="flex items-center justify-between mb-2">
          <div></div>
          <Link to="/forgot-password" className="text-sm text-primary hover:underline">
            Forgot password
          </Link>
        </div>
        
        <Button type="submit" fullWidth isLoading={loading}>
          Log in
        </Button>
      </form>
      
      <div className="text-center mt-4 text-sm text-gray-500">
        Don't have an account?{' '}
        <Link to="/register" className="text-primary hover:underline font-medium">
          Sign up
        </Link>
      </div>
    </Card>
  );
};

export default LoginForm; 